/** Carries an item-comment destination across the project navigation and BOQ lazy mount. */
export interface BOQCommentTarget {
  projectId: string;
  documentId: string;
  itemId: string;
  commentId: string;
}

let pendingTarget: BOQCommentTarget | null = null;
let pendingAcknowledgement: {
  target: BOQCommentTarget;
  resolve: () => void;
  reject: (error: Error) => void;
  timer: number;
} | null = null;
let initialQuery = typeof window !== 'undefined' && (/\/pricing(?:\/|$)/.test(window.location.pathname)
  || window.location.pathname === '/projects')
  ? new URLSearchParams(window.location.search)
  : null;

export function queueBOQCommentTarget(target: BOQCommentTarget): void {
  pendingTarget = target;
  window.dispatchEvent(new Event('boq:comment-target-change'));
}

export function waitForBOQCommentTarget(target: BOQCommentTarget): Promise<void> {
  if (pendingAcknowledgement) {
    window.clearTimeout(pendingAcknowledgement.timer);
    pendingAcknowledgement.reject(new Error('Another comment was opened'));
  }
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      if (pendingAcknowledgement?.target.commentId === target.commentId) pendingAcknowledgement = null;
      reject(new Error('Could not open the comment'));
    }, 15000);
    pendingAcknowledgement = { target, resolve, reject, timer };
  });
}

export function settleBOQCommentTarget(
  target: Pick<BOQCommentTarget, 'documentId' | 'itemId' | 'commentId'>,
  error?: Error,
): void {
  const pending = pendingAcknowledgement;
  if (!pending || pending.target.documentId !== target.documentId
    || pending.target.itemId !== target.itemId || pending.target.commentId !== target.commentId) return;
  window.clearTimeout(pending.timer);
  pendingAcknowledgement = null;
  if (error) pending.reject(error);
  else pending.resolve();
}

export function readBOQCommentTarget(projectId: string): BOQCommentTarget | null {
  if (pendingTarget?.projectId === projectId) return pendingTarget;
  const params = new URLSearchParams(window.location.search);
  if (!params.has('boqCommentId') && initialQuery) {
    for (const key of ['boqProjectId', 'boqDocumentId', 'boqItemId', 'boqCommentId']) {
      const value = initialQuery.get(key);
      if (value) params.set(key, value);
    }
  }
  const documentId = params.get('boqDocumentId');
  const itemId = params.get('boqItemId');
  const commentId = params.get('boqCommentId');
  const routeProjectId = params.get('boqProjectId');
  if (routeProjectId && routeProjectId !== projectId) return null;
  return documentId && itemId && commentId ? { projectId, documentId, itemId, commentId } : null;
}

export function clearBOQCommentTarget(target: BOQCommentTarget): void {
  if (initialQuery?.get('boqCommentId') === target.commentId) initialQuery = null;
  if (pendingTarget?.projectId === target.projectId && pendingTarget.commentId === target.commentId) {
    pendingTarget = null;
  }
  const url = new URL(window.location.href);
  if (url.searchParams.get('boqCommentId') !== target.commentId) return;
  url.searchParams.delete('boqDocumentId');
  url.searchParams.delete('boqProjectId');
  url.searchParams.delete('boqItemId');
  url.searchParams.delete('boqCommentId');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}
